import React, { useEffect, useState } from "react";
import { Webhook, RefreshCw, AlertTriangle, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { ExportCsvButton } from "./ExportCsvButton";
import { DEFAULT_FILTERS, FILTER_MARKETS, filterQuery } from "../lib/adminFilters";

const RESULT_LABEL = { received: "RECEBIDO", ignored_other_product: "IGNORADO — OUTRO PRODUTO", ignored: "IGNORADO", rejected: "REJEITADO — AUTENTICAÇÃO", rejected_authentication: "REJEITADO — AUTENTICAÇÃO", error: "ERRO", duplicate: "DUPLICADO" };
const isError = (r) => ["error", "rejected", "rejected_authentication"].includes(r);
const resultStyle = (r) => (r === "received" ? "bg-emerald-500/15 text-emerald-300" : isError(r) ? "bg-red-500/15 text-red-300" : r === "ignored_other_product" ? "bg-amber-500/15 text-amber-300" : "bg-slate-500/20 text-slate-300");
const maskEmail = (e) => { if (!e) return "—"; const [u, d] = e.split("@"); return d ? `${u.slice(0, 2)}${"*".repeat(Math.max(u.length - 2, 2))}@${d}` : e; };

const badge = (s) => ({
  approved: "bg-emerald-500/15 text-emerald-300", pending: "bg-amber-500/15 text-amber-300",
  refunded: "bg-red-500/15 text-red-300", chargeback: "bg-red-500/15 text-red-300",
  cancelled: "bg-slate-500/20 text-slate-300", refund_requested: "bg-orange-500/15 text-orange-300",
}[s] || "bg-slate-500/20 text-slate-300");

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

export function PaymentsMonitor({ filters = DEFAULT_FILTERS }) {
  const [status, setStatus] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [tab, setTab] = useState("purchases");
  const [manualEmail, setManualEmail] = useState("");
  const [sending, setSending] = useState("");
  const [outbox, setOutbox] = useState({});
  const EMAIL_UI = { sent: ["🟢", "Enviado", "text-emerald-300"], pending: ["🟡", "Pendente", "text-amber-300"], sending: ["🟡", "Enviando", "text-amber-300"], failed: ["🔴", "Falhou", "text-red-300"] };
  const AccessEmailCell = ({ p }) => {
    const o = outbox[p.external_transaction_id];
    if (!o) return <span className="text-xmas-slate" data-testid={`access-email-${p.external_transaction_id}`}>—</span>;
    const [dot, label, cls] = EMAIL_UI[o.status] || ["⚪", o.status, "text-xmas-slate"];
    const tip = [`Tentativas: ${o.attempts}`, o.last_attempt_at && `Última tentativa: ${fmtDate(o.last_attempt_at)}`, o.sent_at && `Enviado em: ${fmtDate(o.sent_at)}`, o.email_id && `ID: ${o.email_id}`, o.error && `Motivo: ${o.error}`].filter(Boolean).join("\n");
    return (
      <span title={tip} className={`cursor-help ${cls}`} data-testid={`access-email-${p.external_transaction_id}`} data-status={o.status}>
        {dot} {label}<br /><span className="text-[10px] text-xmas-slate">{o.attempts}× · {fmtDate(o.sent_at || o.last_attempt_at || o.created_at)}</span>
        {o.status === "failed" && o.error && <><br /><span className="text-[10px] text-red-300/80" data-testid={`access-email-error-${p.external_transaction_id}`}>{o.error.slice(0, 60)}</span></>}
      </span>
    );
  };

  const resend = (email) => {
    if (!email) return;
    setSending(email);
    api.post("/admin/payments/resend-activation", { email })
      .then(({ data }) => { toast.success(`E-mail de criação de senha reenviado para ${data.email} (${data.lang.toUpperCase()})`); setManualEmail(""); load(); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || e.message))
      .finally(() => setSending(""));
  };

  const fq = filterQuery(filters);
  const load = () => Promise.all([
    api.get("/admin/payments/status"), api.get(`/admin/payments/purchases?limit=100&${fq}`), api.get("/admin/payments/webhooks?limit=100"), api.get("/admin/access-emails"),
  ]).then(([s, p, w, o]) => { setStatus(s.data); setPurchases(p.data.purchases); setWebhooks(w.data.webhooks); setOutbox(Object.fromEntries(o.data.items.map((i) => [i.transaction_id, i]))); }).catch(() => {});
  useEffect(() => { load(); }, [fq]); // eslint-disable-line react-hooks/exhaustive-deps
  const filterLabel = [FILTER_MARKETS.find(([k]) => k === filters.market)?.[1], filters.from ? `${filters.from} → ${filters.to || "hoje"}` : filters.preset !== "all" ? "período selecionado" : null].filter(Boolean).join(" · ");
  if (!status) return null;

  const c = status.counts;
  const flags = [
    ["Token webhook", status.webhook.token_configured], ["Chave única", status.webhook.chave_unica_configured],
    ["Códigos do produto", status.webhook.product_codes_configured],
  ];

  return (
    <section className="glass rounded-3xl p-6" data-testid="payments-monitor">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-display text-lg text-xmas-snow"><Webhook className="h-5 w-5 text-xmas-gold" /> Monetizze — Webhook &amp; Transações</h2>
        <div className="flex items-center gap-2">
          <ExportCsvButton path={`/admin/export/sales.csv?${fq}`} fallbackName="vendas.csv" label="Exportar vendas (CSV)" testid="payments-export-csv" />
          <button onClick={load} data-testid="payments-refresh" className="inline-flex items-center gap-1 rounded-full border border-xmas-gold/30 px-3 py-1.5 text-xs text-xmas-snow hover:border-xmas-gold"><RefreshCw className="h-3.5 w-3.5" /> Atualizar</button>
        </div>
      </div>
      <div className="mb-4 grid grid-cols-2 gap-3 text-center sm:grid-cols-4 lg:grid-cols-7" data-testid="payments-counts">
        {[["Compras", c.purchases], ["Aprovadas", c.approved], ["Pendentes", c.pending], ["Reembolsos", c.refunded],
          ["Acessos ativos", c.entitlements_active], ["Webhooks", c.webhooks], ["Erros", c.webhook_errors]].map(([l, v]) => (
          <div key={l} className="rounded-2xl border border-xmas-gold/15 bg-xmas-navy/40 p-3">
            <p className="text-lg font-bold text-xmas-snow">{v}</p><p className="text-[11px] text-xmas-slate">{l}</p>
          </div>
        ))}
      </div>
      <p className="mb-4 flex flex-wrap gap-2 text-xs text-xmas-slate" data-testid="payments-flags">
        <span>Endpoint: <code className="text-xmas-gold">{status.webhook.path}</code></span>
        <span>· Edição {status.webhook.edition} · SKU {status.webhook.product_sku}</span>
        {flags.map(([l, ok]) => <span key={l} className={ok ? "text-emerald-300" : "text-amber-300"}>· {l}: {ok ? "configurado" : "não configurado"}</span>)}
        <span>· Último webhook: {fmtDate(status.last_webhook_at)}</span>
        <span>· Alertas por e-mail para: <b className={status.alerts?.recipient ? "text-emerald-300" : "text-amber-300"}>{status.alerts?.recipient || "não configurado"}</b></span>
      </p>
      {status.alerts?.recent?.length > 0 && (
        <div className="mb-4 rounded-2xl border border-red-400/30 bg-red-500/5 p-4" data-testid="payments-alerts">
          <div className="mb-2 flex items-center justify-between">
            <p className="flex items-center gap-2 text-sm font-semibold text-red-200"><AlertTriangle className="h-4 w-4" /> Alertas ({status.alerts.unread} não lidos)</p>
            {status.alerts.unread > 0 && (
              <button data-testid="payments-alerts-read" onClick={() => api.post("/admin/payments/alerts/read").then(load)} className="text-xs text-xmas-slate hover:text-xmas-gold">Marcar como lidos</button>
            )}
          </div>
          <ul className="space-y-1 text-xs">
            {status.alerts.recent.slice(0, 8).map((a, i) => (
              <li key={i} className={`flex flex-wrap gap-x-3 ${a.read ? "text-xmas-slate" : "text-xmas-snow"}`} data-testid={`payments-alert-${i}`}>
                <span className="text-xmas-slate">{fmtDate(a.at)}</span><span className="font-semibold">{a.title}</span>
                <span>{Object.entries(a.details || {}).map(([k, v]) => `${k}: ${v}`).join(" · ")}</span>
                {!a.emailed && <span className="text-xmas-slate">(agrupado)</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {["purchases", "webhooks"].map((k) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`payments-tab-${k}`}
            className={`rounded-full px-4 py-1.5 text-xs ${tab === k ? "bg-xmas-gold text-xmas-navy" : "border border-xmas-gold/30 text-xmas-snow"}`}>
            {k === "purchases" ? `Compras (${purchases.length})` : "Webhooks recebidos"}
          </button>
        ))}
        <form className="ml-auto flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); resend(manualEmail); }} data-testid="resend-form">
          <input type="email" required value={manualEmail} onChange={(e) => setManualEmail(e.target.value)} placeholder="e-mail do comprador"
            data-testid="resend-email-input" className="rounded-full border border-xmas-gold/20 bg-xmas-navy/50 px-3 py-1.5 text-xs text-xmas-snow outline-none focus:border-xmas-gold" />
          <button type="submit" disabled={sending === manualEmail} data-testid="resend-submit" className="inline-flex items-center gap-1 rounded-full border border-xmas-gold/40 px-3 py-1.5 text-xs text-xmas-gold hover:bg-xmas-gold/10 disabled:opacity-50">
            <Send className="h-3.5 w-3.5" /> Reenviar ativação
          </button>
        </form>
      </div>
      <div className="overflow-x-auto">
        {tab === "purchases" ? (
          <table className="w-full text-left text-xs" data-testid="purchases-table">
            <thead className="text-xmas-slate"><tr><th className="py-2">Transação</th><th>Comprador</th><th>Status</th><th>Valor</th><th>País/Mercado</th><th>Idioma</th><th>Aprovada em</th><th>Atualizada</th><th>E-mail de acesso</th><th></th></tr></thead>
            <tbody>
              {purchases.length === 0 && <tr><td colSpan={10} className="py-4 text-center text-xmas-slate" data-testid="purchases-empty">Nenhuma compra{filterLabel ? ` para o filtro (${filterLabel})` : " recebida ainda"}.</td></tr>}
              {purchases.map((p) => (
                <tr key={p.external_transaction_id} className="border-t border-white/5 text-xmas-snow" data-testid={`purchase-${p.external_transaction_id}`}>
                  <td className="py-2 font-mono">{p.external_transaction_id}</td>
                  <td>{p.buyer_email}<br /><span className="text-xmas-slate">{p.buyer_name}</span></td>
                  <td><span className={`rounded-full px-2 py-0.5 ${badge(p.status)}`}>{p.status}</span><br /><span className="text-xmas-slate">{p.status_provider}</span></td>
                  <td>{p.amount != null ? `${p.amount} ${p.currency || ""}` : "—"}</td>
                  <td>{p.country || "—"} / {p.market || "—"}</td>
                  <td>{p.lang}</td>
                  <td>{fmtDate(p.approved_at)}</td>
                  <td>{fmtDate(p.updated_at)}</td>
                  <td><AccessEmailCell p={p} /></td>
                  <td>
                    {p.status === "approved" && (
                      <button onClick={() => resend(p.buyer_email)} disabled={sending === p.buyer_email} data-testid={`resend-${p.external_transaction_id}`}
                        title="Reenviar e-mail de criação de senha" className="inline-flex items-center gap-1 rounded-full border border-xmas-gold/30 px-2 py-1 text-[11px] text-xmas-gold hover:bg-xmas-gold/10 disabled:opacity-50">
                        <Send className="h-3 w-3" /> Reenviar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="w-full text-left text-xs" data-testid="webhooks-table">
            <thead className="text-xmas-slate"><tr><th className="py-2">Recebido</th><th>Evento / status</th><th>Status interno</th><th>Transação</th><th>Produto (código · chave · nome)</th><th>SKU</th><th>E-mail</th><th>Resultado</th><th>Motivo / erro</th></tr></thead>
            <tbody>
              {webhooks.length === 0 && <tr><td colSpan={9} className="py-4 text-center text-xmas-slate">Nenhum webhook recebido ainda.</td></tr>}
              {webhooks.map((w) => (
                <tr key={w.event_id} className="border-t border-white/5 align-top text-xmas-snow" data-testid={`webhook-${w.event_id}`}>
                  <td className="py-2 whitespace-nowrap">{fmtDate(w.received_at)}</td>
                  <td>{w.event_code ?? "—"} <span className="text-xmas-slate">{w.raw_status}</span></td>
                  <td><span className={`rounded-full px-2 py-0.5 ${badge(w.internal_status)}`}>{w.internal_status}</span></td>
                  <td className="font-mono">{w.external_transaction_id || "—"}</td>
                  <td className={w.product_check === "other" ? "text-amber-300" : ""}>
                    <span className="font-mono">{w.product_code || "—"}</span>
                    {w.product_key && <span className="font-mono text-xmas-slate"> · {w.product_key.slice(0, 8)}…</span>}
                    {w.product_name && <><br /><span className="text-xmas-slate">{w.product_name}</span></>}
                  </td>
                  <td className={`font-mono ${w.sku_received === "MYCHRISTMAS2026" ? "text-emerald-300" : "text-xmas-slate"}`}>{w.sku_received || "—"}</td>
                  <td>{maskEmail(w.buyer_email)}</td>
                  <td><span className={`rounded-full px-2 py-0.5 ${resultStyle(w.result)}`} data-testid={`webhook-result-${w.event_id}`}>{RESULT_LABEL[w.result] || w.result}</span>{w.access ? ` · ${w.access}` : ""}{w.duplicate ? " · duplicado" : ""}</td>
                  <td className={`max-w-[260px] ${w.result === "received" ? "text-xmas-slate" : w.result === "ignored_other_product" ? "text-amber-200" : isError(w.result) ? "text-red-300" : "text-xmas-slate"}`}>
                    {w.result === "ignored_other_product" ? <>Produto diferente do My Christmas.<br /><span className="text-xmas-slate">{w.reason}</span></> : (w.reason || w.error || "")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
